import { useState } from "react"

import { toast } from "sonner"

import { useCreateCustomer, useUpdateCustomer } from "@/hooks/useCollections"
import { Customer } from "@/types/collections"

import { CustomerFormData } from "./types"

export function useCustomerDialog(refetchCustomers: () => unknown) {
  const createCustomer = useCreateCustomer()
  const updateCustomer = useUpdateCustomer()

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<Customer | null>(null)
  const [form, setForm] = useState<Partial<CustomerFormData>>({})

  const openDialog = (customer?: Customer) => {
    if (customer) {
      setEditing(customer)
      setForm({
        name: customer.name,
        account_id: customer.account_id,
        customer_type: customer.customer_type,
        status: customer.status,
      })
    } else {
      setEditing(null)
      setForm({
        name: "",
        account_id: "",
        customer_type: "shipper",
        status: "active",
      })
    }
    setOpen(true)
  }

  const save = async () => {
    try {
      if (editing) {
        await updateCustomer.mutateAsync({
          id: editing.id,
          data: form,
        })
        toast.success("Customer updated successfully")
      } else {
        await createCustomer.mutateAsync(form as Customer)
        toast.success("Customer created successfully")
      }
      setOpen(false)
      refetchCustomers()
    } catch (error) {
      toast.error("Error saving customer", {
        description: error instanceof Error ? error.message : "Unknown error",
      })
    }
  }

  const isSaving = createCustomer.isPending || updateCustomer.isPending

  return { open, setOpen, editing, form, setForm, openDialog, save, isSaving }
}

export type CustomerDialogState = ReturnType<typeof useCustomerDialog>
